import { useState, useEffect, useCallback } from "react";
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement,
    LineElement, ArcElement, Filler, Tooltip, Legend,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Overview.css";
import api from "../../axiosInstance";
import { useApp } from "../../AppProvider";
import { useAuth } from "../../AuthProvider";
import EditBookedAppointment from "../../Components/Appointment/EditBookedAppointment";
import logger from "../../utils/logger";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Filler, Tooltip, Legend);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const INR = (n) => n == null ? "—" : "₹" + Math.round(n).toLocaleString("en-IN");
const fmtN = (n) => n == null ? "—" : n.toLocaleString("en-IN");
const localDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
const todayStr = () => localDate(new Date());
const startOf = (unit) => {
    const d = new Date();
    if (unit === "week") { d.setDate(d.getDate() - d.getDay()); return localDate(d); }
    if (unit === "month") return localDate(new Date(d.getFullYear(), d.getMonth(), 1));
    if (unit === "quarter") { const q = Math.floor(d.getMonth() / 3); return localDate(new Date(d.getFullYear(), q * 3, 1)); }
    if (unit === "year") return localDate(new Date(d.getFullYear(), 0, 1));
    return todayStr();
};
const endOf = (unit) => {
    const d = new Date();
    if (unit === "week") { d.setDate(d.getDate() - d.getDay() + 6); return localDate(d); }
    if (unit === "month") return localDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    if (unit === "quarter") { const q = Math.floor(d.getMonth() / 3); return localDate(new Date(d.getFullYear(), q * 3 + 3, 0)); }
    if (unit === "year") return localDate(new Date(d.getFullYear(), 11, 31));
    return todayStr();
};
const fmtName = (n) => {
    if (!n) return "—";
    if (typeof n === "string") return n;
    return [n.firstName, n.lastName].filter(Boolean).join(" ") || "—";
};
const fmtApptDateTime = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" })
        + ", " + dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

// ─── Constants ────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
    { bg: "#e1f5ee", fg: "#0f6e56" },
    { bg: "#dbeafe", fg: "#185fa5" },
    { bg: "#fef3c7", fg: "#b45309" },
    { bg: "#dcfce7", fg: "#166534" },
    { bg: "#fbeaf0", fg: "#72243e" },
    { bg: "#e6f1fb", fg: "#0c447c" },
];

const CONDITION_COLORS = ["#1d9e75", "#378add", "#f5c842", "#d4537e", "#8b5cf6", "#f97316"];



const STATUS_STYLE = {
    upcoming: { background: "#dbeafe", color: "#1e40af", label: "Upcoming" },
    progress: { background: "#fef9c3", color: "#854d0e", label: "In Progress" },
    done: { background: "#dcfce7", color: "#166534", label: "Completed" },
    cancelled: { background: "#fee2e2", color: "#991b1b", label: "Cancelled" },
    pending: { background: "#fef3c7", color: "#92400e", label: "Pending" },
};

const isApptTimePast = (scheduledTime) => {
    if (!scheduledTime) return false;
    try {
        const match = String(scheduledTime).match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
        if (!match) return false;
        let h = parseInt(match[1], 10);
        const min = parseInt(match[2], 10);
        const period = (match[3] || "").toUpperCase();
        if (period === "PM" && h !== 12) h += 12;
        if (period === "AM" && h === 12) h = 0;
        const apptTime = new Date();
        apptTime.setHours(h, min, 0, 0);
        return apptTime < new Date();
    } catch {
        return false;
    }
};

// ─── Appointment-list constants & styles ─────────────────────────────────────
const APPT_PILL = {
    ACCEPTED: { bg: "#dbeafe", color: "#1e40af", label: "Accepted" },
    UPCOMING: { bg: "#dbeafe", color: "#1e40af", label: "Upcoming" },
    "IN-PROGRESS": { bg: "#fef9c3", color: "#854d0e", label: "In Progress" },
    "FOR-TESTING": { bg: "#f3e8ff", color: "#7e22ce", label: "Testing" },
    COMPLETED: { bg: "#dcfce7", color: "#166534", label: "Completed" },
    CANCELLED: { bg: "#fee2e2", color: "#991b1b", label: "Cancelled" },
};

const OA = {
    page: { display: "flex", flexDirection: "column", gap: 16 },
    header: { display: "flex", alignItems: "center", gap: 12 },
    backBtn: { display: "flex", alignItems: "center", gap: 5, height: 32, padding: "0 12px", background: "#fff", border: "0.5px solid #e8eaed", borderRadius: 8, fontSize: 13, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" },
    title: { fontSize: 20, fontWeight: 600, color: "#1a2a3a", margin: 0 },
    badge: { fontSize: 12, color: "#6b7280", background: "#f2f4f7", borderRadius: 20, padding: "3px 10px", fontWeight: 500 },

    filterBar: { display: "flex", alignItems: "center", gap: 12, background: "#fff", border: "0.5px solid #d7e3f5", borderRadius: 12, padding: "12px 16px", flexWrap: "wrap" },
    fGroup: { display: "flex", alignItems: "center", gap: 8, position: "relative" },
    fLabel: { fontSize: 12, color: "#a0aec0", fontWeight: 500, whiteSpace: "nowrap" },
    fInput: { height: 34, border: "0.5px solid #bcc3ca", borderRadius: 8, padding: "0 8px", fontSize: 13, color: "#9d9d9d", background: "#fff", outline: "none", fontFamily: "inherit" },
    fSelect: { height: 34, border: "0.5px solid #bcc3ca", borderRadius: 8, padding: "0 15px", fontSize: 13, color: "#9d9d9d", background: "#fff", outline: "none", fontFamily: "inherit", cursor: "pointer" },
    fDivider: { width: 1, height: 20, background: "#2d3f52", flexShrink: 0 },
    dateRow: { display: "flex", alignItems: "center", gap: 6 },
    dateSep: { fontSize: 12, color: "#a0aec0" },
    btnApply: { height: 34, padding: "0 16px", background: "#f5c842", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, color: "#1a2a3a", cursor: "pointer", fontFamily: "inherit" },
    btnReset: { height: 34, padding: "0 12px", background: "transparent", border: "0.5px solid #2d3f52", borderRadius: 8, fontSize: 13, color: "#a0aec0", cursor: "pointer", fontFamily: "inherit" },

    // Patient autocomplete
    patientWrap: { position: "relative", minWidth: 190 },
    patientChip: { display: "flex", alignItems: "center", gap: 6, height: 34, padding: "0 8px 0 12px", background: "#1a2a3a", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#fff", whiteSpace: "nowrap" },
    chipClear: { width: 18, height: 18, border: "none", borderRadius: 4, background: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 0 },
    searchWrap: { position: "relative", display: "flex", alignItems: "center" },
    searchIcon: { position: "absolute", left: 9, pointerEvents: "none" },
    searchInput: { height: 34, border: "0.5px solid #bcc3ca", borderRadius: 8, padding: "0 10px 0 28px", fontSize: 13, color: "#1a2a3a", background: "#fff", outline: "none", fontFamily: "inherit", width: 180 },
    dropdown: { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, minWidth: 220, background: "#fff", border: "0.5px solid #e8eaed", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", zIndex: 50, maxHeight: 240, overflowY: "auto" },
    dropOpt: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", cursor: "pointer", gap: 8, borderTop: "0.5px solid #f5f5f5" },
    dropOptName: { fontSize: 13, fontWeight: 500, color: "#1a2a3a" },
    dropOptPhone: { fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" },
    dropMuted: { padding: "12px", textAlign: "center", fontSize: 12, color: "#9ca3af" },

    tableCard: { background: "#fff", border: "0.5px solid #e8eaed", borderRadius: 12, padding: 18, overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    th: { color: "#6b7280", fontWeight: 500, textAlign: "left", padding: "6px 8px", borderBottom: "0.5px solid #e8eaed", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" },
    td: { padding: "9px 8px", borderBottom: "0.5px solid #f5f5f5", color: "#1a2a3a" },
    tdMuted: { padding: "9px 8px", borderBottom: "0.5px solid #f5f5f5", color: "#6b7280", fontSize: 12 },
    tdEmpty: { padding: "18px 8px", textAlign: "center", color: "#9ca3af", fontSize: 13 },
    editBtn: { display: "inline-flex", alignItems: "center", gap: 4, height: 26, padding: "0 10px", border: "0.5px solid #e8eaed", borderRadius: 6, background: "#fff", fontSize: 12, color: "#378add", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
    waBtn: { display: "inline-flex", alignItems: "center", gap: 4, height: 26, padding: "0 10px", border: "0.5px solid #d1fae5", borderRadius: 6, background: "#f0fdf4", fontSize: 12, color: "#16a34a", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
    waBtnBusy: { display: "inline-flex", alignItems: "center", gap: 4, height: 26, padding: "0 10px", border: "0.5px solid #e8eaed", borderRadius: 6, background: "#f9fafb", fontSize: 12, color: "#9ca3af", cursor: "not-allowed", fontFamily: "inherit", whiteSpace: "nowrap", opacity: 0.6 },
    actionCell: { padding: "9px 8px", borderBottom: "0.5px solid #f5f5f5", display: "flex", alignItems: "center", gap: 6 },

    pagination: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "8px 0 4px" },
    pageBtn: { width: 30, height: 30, border: "0.5px solid #e8eaed", borderRadius: 7, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" },
    pageInfo: { fontSize: 13, color: "#6b7280" },
};

function ApptStatusPill({ status }) {
    const s = (status || "").toUpperCase();
    const p = APPT_PILL[s] || { bg: "#f3f4f6", color: "#6b7280", label: status || "—" };
    return (
        <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 500, background: p.bg, color: p.color }}>
            {p.label}
        </span>
    );
}

// ─── AllAppointments ──────────────────────────────────────────────────────────
function AllAppointments({ onBack }) {
    const { selectedClinic } = useApp();

    const [patientQuery, setPatientQuery] = useState("");
    const [patientResults, setPatientResults] = useState([]);
    const [patientSearching, setPatientSearching] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);

    const [statusFilter, setStatusFilter] = useState("all");
    const [dateFrom, setDateFrom] = useState(todayStr());
    const [dateTo, setDateTo] = useState(todayStr());
    const [quick, setQuick] = useState("today");
    const [page, setPage] = useState(1);
    const LIMIT = 15;

    // Maps UI status values to the correct API query param
    const STATUS_PARAM = {
        UPCOMING: { param: "status", val: "UPCOMING" },
        COMPLETED: { param: "status", val: "COMPLETED" },
        CANCELLED: { param: "status", val: "CANCELLED" },
        PENDING: { param: "appointmentStatus", val: "PENDING" },
        ACCEPTED: { param: "appointmentStatus", val: "ACCEPTED" },
        REJECTED: { param: "appointmentStatus", val: "REJECTED" },
    };

    const [applied, setApplied] = useState({
        patientId: null, status: "all", startDate: todayStr(), endDate: todayStr(),
    });

    const [apptList, setApptList] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [editTask, setEditTask] = useState(null);
    const [waSending, setWaSending] = useState(null); // appointmentId being sent

    const sendWaReminder = async (appointmentId) => {
        setWaSending(appointmentId);
        try {
            await api.post(`/wa/appointmentReminder/${appointmentId}`);
        } catch (err) {
            logger.error("WA reminder error:", err);
        } finally {
            setWaSending(null);
        }
    };

    // ── Patient search debounce ──
    useEffect(() => {
        if (patientQuery.length < 3 || selectedPatient) { setPatientResults([]); return; }
        setPatientSearching(true);
        const t = setTimeout(async () => {
            try {
                const res = await api.get("/search/patients", { params: { searchQuery: patientQuery } });
                setPatientResults(res.data || []);
            } catch (err) { logger.error(err); }
            finally { setPatientSearching(false); }
        }, 300);
        return () => clearTimeout(t);
    }, [patientQuery, selectedPatient]);

    // ── Fetch appointments ──
    const fetchAppts = useCallback(async (params) => {
        setLoading(true);
        try {
            logger.debug(params)
            const res = await api.get("/appointments", { params });
            logger.debug('res', res)
            const rows = res.data.data;

            setApptList(Array.isArray(rows) ? rows : []);
            setTotal(res.data.totalCount ?? 0);
        } catch (err) { logger.error("Appointments fetch error:", err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        const p = { page, limit: LIMIT };
        if (selectedClinic?._id) p.clinicId = selectedClinic._id;
        if (applied.startDate) p.startDate = applied.startDate;
        if (applied.endDate) p.endDate = applied.endDate;
        if (applied.patientId) p.patientId = applied.patientId;
        if (applied.status !== "all") {
            const mapping = STATUS_PARAM[applied.status];
            if (mapping) p[mapping.param] = mapping.val;
        }
        fetchAppts(p);
    }, [applied, page, fetchAppts, refreshKey, selectedClinic]);

    // ── Filter actions ──
    const handleQuick = (val) => {
        setQuick(val);
        if (val === "today") { setDateFrom(todayStr()); setDateTo(todayStr()); }
        else if (val === "week") { setDateFrom(startOf("week")); setDateTo(endOf("week")); }
        else if (val === "month") { setDateFrom(startOf("month")); setDateTo(endOf("month")); }
    };

    const applyFilters = () => {
        setPage(1);
        setApplied({ patientId: selectedPatient?._id || null, status: statusFilter, startDate: dateFrom, endDate: dateTo });
    };

    const resetFilters = () => {
        setPatientQuery(""); setSelectedPatient(null); setPatientResults([]);
        setStatusFilter("all"); setQuick("today");
        setDateFrom(todayStr()); setDateTo(todayStr()); setPage(1);
        setApplied({ patientId: null, status: "all", startDate: todayStr(), endDate: todayStr() });
    };

    // ── Open edit (no extra fetch needed — appointment has all fields) ──
    const openEdit = (appt) => {
        setEditTask({
            id: appt._id,
            medicalExpenditureId: appt.medicalExpenditureId || null,
            patientId: appt.patientId,
            content: appt.patientName || "Patient",
            time: appt.dateOfAppointment
                ? new Date(appt.dateOfAppointment).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                : "—",
        });
    };

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    return (
        <div style={OA.page}>
            {/* Header */}
            <div style={OA.header}>
                <button style={OA.backBtn} onClick={onBack}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Back
                </button>
                <h1 style={OA.title}>All Appointments</h1>
                {total > 0 && <span style={OA.badge}>{total} appointments</span>}
            </div>

            {/* Filter Bar */}
            <div style={OA.filterBar}>
                {/* Patient autocomplete */}
                <div style={OA.fGroup}>
                    <span style={OA.fLabel}>Patient</span>
                    <div style={OA.patientWrap}>
                        {selectedPatient ? (
                            <div style={OA.patientChip}>
                                <span>{fmtName(selectedPatient.name)}</span>
                                <button style={OA.chipClear} onClick={() => { setSelectedPatient(null); setPatientQuery(""); }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <>
                                <div style={OA.searchWrap}>
                                    <svg style={OA.searchIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
                                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    <input type="text" style={OA.searchInput} placeholder="Search patient…"
                                        value={patientQuery} onChange={e => setPatientQuery(e.target.value)} />
                                </div>
                                {(patientSearching || patientResults.length > 0) && (
                                    <div style={OA.dropdown}>
                                        {patientSearching
                                            ? <div style={OA.dropMuted}>Searching…</div>
                                            : patientResults.map((p, i) => (
                                                <div key={p._id || i} style={OA.dropOpt}
                                                    onMouseEnter={e => e.currentTarget.style.background = "#f8fbfc"}
                                                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                                                    onClick={() => { setSelectedPatient(p); setPatientQuery(""); setPatientResults([]); }}>
                                                    <span style={OA.dropOptName}>{fmtName(p.name)}</span>
                                                    <span style={OA.dropOptPhone}>{p.contact?.phoneNumber?.phoneNumber || ""}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div style={OA.fDivider} />

                {/* Status */}
                {/* <div style={OA.fGroup}>
                    <span style={OA.fLabel}>Status</span>
                    <select style={OA.fSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="all">All</option>
                        <optgroup label="Appointment Status">
                            <option value="UPCOMING">Upcoming</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="CANCELLED">Cancelled</option>
                        </optgroup>
                        <optgroup label="Booking Status">
                            <option value="PENDING">Pending</option>
                            <option value="ACCEPTED">Accepted</option>
                            <option value="REJECTED">Rejected</option>
                        </optgroup>
                    </select>
                </div>

                <div style={OA.fDivider} /> */}

                {/* Date range */}
                <div style={OA.fGroup}>
                    <span style={OA.fLabel}>Date</span>
                    <div style={OA.dateRow}>
                        <DatePicker
                            selected={dateFrom ? new Date(dateFrom + "T00:00:00") : null}
                            onChange={d => { if (d) { setDateFrom(localDate(d)); setQuick(""); } }}
                            dateFormat="dd MMM yyyy"
                            placeholderText="From"
                            className="ov-filter-input ov-datepicker-input"
                            calendarClassName="ov-datepicker-calendar"
                            showPopperArrow={false}
                            maxDate={dateTo ? new Date(dateTo + "T00:00:00") : null}
                        />
                        <span style={OA.dateSep}>to</span>
                        <DatePicker
                            selected={dateTo ? new Date(dateTo + "T00:00:00") : null}
                            onChange={d => { if (d) { setDateTo(localDate(d)); setQuick(""); } }}
                            dateFormat="dd MMM yyyy"
                            placeholderText="To"
                            className="ov-filter-input ov-datepicker-input"
                            calendarClassName="ov-datepicker-calendar"
                            showPopperArrow={false}
                            minDate={dateFrom ? new Date(dateFrom + "T00:00:00") : null}
                        />
                    </div>
                    <select style={OA.fSelect} value={quick} onChange={e => handleQuick(e.target.value)}>
                        <option value="">Quick select</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                    </select>
                </div>

                <div style={OA.fDivider} />
                <button style={OA.btnApply} onClick={applyFilters}>Apply</button>
                <button style={OA.btnReset} onClick={resetFilters}>Reset</button>
            </div>

            {/* Table */}
            <div style={OA.tableCard}>
                <table style={OA.table}>
                    <thead>
                        <tr>
                            {["Patient", "Type", "Doctor", "Date & Time", "Status", ""].map(h => (
                                <th key={h} style={OA.th}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} style={OA.tdEmpty}>Loading…</td></tr>
                        ) : apptList.length === 0 ? (
                            <tr><td colSpan={6} style={OA.tdEmpty}>No appointments found</td></tr>
                        ) : apptList.map((a, i) => (
                            <tr key={a._id || i}
                                onMouseEnter={e => [...e.currentTarget.cells].forEach(c => c.style.background = "#f8fbfc")}
                                onMouseLeave={e => [...e.currentTarget.cells].forEach(c => c.style.background = "")}>
                                <td style={OA.td}>{a.patientName || "—"}</td>
                                <td style={OA.tdMuted}>{a.appointmentName || a.tags?.[0] || "—"}</td>
                                <td style={OA.tdMuted}>{a.doctorName || "—"}</td>
                                <td style={OA.tdMuted}>{fmtApptDateTime(a.dateOfAppointment)}</td>
                                <td style={OA.td}><ApptStatusPill status={a.appointmentStatus} /></td>
                                <td style={{ ...OA.td, whiteSpace: "nowrap" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <button style={OA.editBtn} onClick={() => openEdit(a)}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                            Edit
                                        </button>
                                        {new Date(a.dateOfAppointment) > new Date() && (
                                            <button
                                                style={waSending === a._id ? OA.waBtnBusy : OA.waBtn}
                                                disabled={waSending === a._id}
                                                onClick={() => sendWaReminder(a._id)}
                                            >
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                                </svg>
                                                {waSending === a._id ? "Sending…" : "WA"}
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={OA.pagination}>
                    <button style={{ ...OA.pageBtn, opacity: page === 1 ? 0.35 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}
                        disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>
                    <span style={OA.pageInfo}>Page {page} of {totalPages}</span>
                    <button style={{ ...OA.pageBtn, opacity: page >= totalPages ? 0.35 : 1, cursor: page >= totalPages ? "not-allowed" : "pointer" }}
                        disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                </div>
            )}

            {/* Edit popup */}
            {editTask && (
                <EditBookedAppointment
                    task={editTask}
                    setEditModalOpen={(open) => {
                        if (!open) { setEditTask(null); setRefreshKey(k => k + 1); }
                    }}
                />
            )}
        </div>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function MetricCard({ iconBg, iconStroke, iconPath, label, value, change, changeType, loading }) {
    return (
        <div className="ov-metric-card">
            <div className="ov-metric-icon" style={{ background: iconBg }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke={iconStroke} strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round">
                    {iconPath}
                </svg>
            </div>
            <div className="ov-metric-label">{label}</div>
            <div className="ov-metric-value">
                {loading ? <span className="ov-skeleton">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span> : value}
            </div>
            <div className={`ov-metric-change ov-change-${changeType}`}>
                {loading ? "" : change}
            </div>
        </div>
    );
}

function Avatar({ initials, bg, fg, size = 34 }) {
    return (
        <div className="ov-avatar" style={{ width: size, height: size, background: bg, color: fg, fontSize: size * 0.35 }}>
            {initials}
        </div>
    );
}

function StatusPill({ status }) {
    const st = STATUS_STYLE[status] || STATUS_STYLE.upcoming;
    return (
        <span className="ov-status-pill" style={{ background: st.background, color: st.color }}>
            {st.label}
        </span>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Overview() {
    const { selectedClinic } = useApp();
    const { user } = useAuth();

    const [greeting, setGreeting] = useState("Good morning");

    const [dashLoading, setDashLoading] = useState(true);
    const [dashboard, setDashboard] = useState(null);
    const [visitsLoading, setVisitsLoading] = useState(true);
    const [visitsData, setVisitsData] = useState(null);
    const [visitPeriod, setVisitPeriod] = useState("week");
    const [view, setView] = useState("dashboard");
    const [dateFrom, setDateFrom] = useState(startOf("month"));
    const [dateTo, setDateTo] = useState(todayStr());
    const [quick, setQuick] = useState("month");
    const [applied, setApplied] = useState({ startDate: startOf("month"), endDate: todayStr() });

    useEffect(() => {
        const hr = new Date().getHours();
        setGreeting(hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening");
    }, []);

    const fetchDashboard = useCallback(async (clinicId, startDate, endDate) => {
        setDashLoading(true);
        try {
            const today = todayStr();

            const apptParams = { startDate: today, endDate: today, limit: 1000 };
            if (clinicId) apptParams.clinicId = clinicId;

            const revParams = { from: startDate || today, to: endDate || today };
            if (clinicId) revParams.clinicId = clinicId;

            const ovParams = {};
            if (clinicId) ovParams.clinicId = clinicId;
            if (startDate) ovParams.startDate = startDate;
            if (endDate) ovParams.endDate = endDate;

            const [apptRes, revRes, ovRes] = await Promise.allSettled([
                api.get("/appointments", { params: apptParams }),
                api.get("/medicalExpenditures/dashboard", { params: revParams }),
                api.get("/overview/dashboard", { params: ovParams }),
            ]);

            // Appointments today
            let appointmentsToday = { total: 0, completed: 0, upcoming: 0 };
            let totalPatients = null;
            if (apptRes.status === "fulfilled") {
                const rows = apptRes.value.data.data || [];
                const totalToday = apptRes.value.data.totalCount ?? rows.length;
                const completed = rows.filter(a => a.status === "COMPLETED").length;
                const upcoming = rows.filter(a => !["COMPLETED", "CANCELLED"].includes(a.status)).length;
                appointmentsToday = { total: totalToday, completed, upcoming };
                const uniquePids = new Set(rows.map(a => a.patientId?.toString()).filter(Boolean));
                if (uniquePids.size > 0) totalPatients = uniquePids.size;
            }

            // Revenue + billing metrics
            let revenueMtd = { amount: null, changePercent: null };
            let reportsPending = { count: null, deltaSinceYesterday: null };
            if (revRes.status === "fulfilled") {
                const rm = revRes.value.data.data?.metrics || {};
                revenueMtd = { amount: rm.totalRevenue?.amount, changePercent: rm.totalRevenue?.changePercent };
                reportsPending = { count: rm.pendingBills?.count ?? null, deltaSinceYesterday: null };
                if (rm.patientsBilled?.count != null) totalPatients = rm.patientsBilled.count;
            }

            // Lower sections (appointment list, top drugs, conditions) from overview endpoint if available
            const ovData = ovRes.status === "fulfilled" ? (ovRes.value.data || {}) : {};

            setDashboard({
                ...ovData,
                metrics: {
                    totalPatients: { count: totalPatients },
                    appointmentsToday,
                    reportsPending,
                    revenueMtd,
                },
            });
        } catch (err) {
            logger.error("Overview dashboard error:", err);
        } finally {
            setDashLoading(false);
        }
    }, []);

    const fetchVisits = useCallback(async (period, clinicId) => {
        setVisitsLoading(true);
        try {
            const params = { period };
            if (clinicId) params.clinicId = clinicId;
            const res = await api.get("/overview/patient-visits", { params });
            setVisitsData(res.data);
        } catch (err) {
            logger.error("Patient visits error:", err);
        } finally {
            setVisitsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboard(selectedClinic?._id, applied.startDate, applied.endDate);
    }, [selectedClinic, fetchDashboard, applied]);

    useEffect(() => {
        fetchVisits(visitPeriod, selectedClinic?._id);
    }, [visitPeriod, selectedClinic, fetchVisits]);

    // ── Derived display values ──
    const m = dashboard?.metrics;
    const breakdown = dashboard?.appointmentTypeBreakdown || [];
    const appts = dashboard?.todayAppointments || [];
    const topDrugs = dashboard?.topDrugs || [];
    const conditions = dashboard?.topConditions || [];
    const beds = dashboard?.bedOccupancy || [];

    const doctorName = user ? `${user.name?.firstName || ""} ${user.name?.lastName || ""}`.trim() : "Doctor";
    const clinicName = selectedClinic?.name?.fullName || selectedClinic?.name?.shortName || "your clinic";

    // ── Visit chart ──
    const visitChartData = {
        labels: visitsData?.labels || [],
        datasets: [
            {
                label: "Visits",
                data: visitsData?.visits || [],
                borderColor: "#1d9e75", backgroundColor: "rgba(29,158,117,0.08)",
                tension: 0.4, fill: true, pointRadius: 3, pointBackgroundColor: "#1d9e75", borderWidth: 2,
            },
        ],
    };
    const visitChartOpts = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 11 }, autoSkip: false } },
            y: { grid: { color: "#f0f0f0" }, ticks: { font: { size: 11 } } },
        },
    };

    // ── Appointment types donut ──
    const apptChartData = {
        labels: breakdown.map(b => b.type),
        datasets: [{
            data: breakdown.map(b => b.percent),
            backgroundColor: breakdown.map((_, i) => CONDITION_COLORS[i % CONDITION_COLORS.length]),
            borderWidth: 0,
            hoverOffset: 4,
        }],
    };
    const apptChartOpts = {
        responsive: true, maintainAspectRatio: false, cutout: "62%",
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: c => `${c.label}: ${c.parsed}%` } },
        },
    };

    if (view === "appointments") {
        return (
            <div className="overview__main-container">
                <div className="ov-main-wrapper">
                    <main className="ov-content">
                        <AllAppointments onBack={() => setView("dashboard")} />
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="overview__main-container">
            <div className="ov-main-wrapper">
                <main className="ov-content">

                    {/* ── Greeting ── */}
                    <div className="ov-greeting">
                        <div>
                            <h2 className="ov-greet-title">{greeting}, Dr. {doctorName}</h2>
                            <p className="ov-greet-sub">Here's what's happening at {clinicName} today.</p>
                        </div>
                    </div>

                    {/* ── Date Filter Bar ── */}
                    <div className="ov-filter-bar">
                        <div className="ov-filter-group">
                            <span className="ov-filter-label">Date Range</span>
                            <div className="ov-date-range">
                                <DatePicker
                                    selected={dateFrom ? new Date(dateFrom + "T00:00:00") : null}
                                    onChange={d => { if (d) { setDateFrom(localDate(d)); setQuick(""); } }}
                                    dateFormat="dd MMM yyyy"
                                    placeholderText="From"
                                    className="ov-filter-input ov-datepicker-input"
                                    calendarClassName="ov-datepicker-calendar"
                                    showPopperArrow={false}
                                    maxDate={dateTo ? new Date(dateTo + "T00:00:00") : null}
                                />
                                <span className="ov-date-sep">to</span>
                                <DatePicker
                                    selected={dateTo ? new Date(dateTo + "T00:00:00") : null}
                                    onChange={d => { if (d) { setDateTo(localDate(d)); setQuick(""); } }}
                                    dateFormat="dd MMM yyyy"
                                    placeholderText="To"
                                    className="ov-filter-input ov-datepicker-input"
                                    calendarClassName="ov-datepicker-calendar"
                                    showPopperArrow={false}
                                    minDate={dateFrom ? new Date(dateFrom + "T00:00:00") : null}
                                />
                            </div>
                            <select className="ov-filter-select" value={quick}
                                onChange={e => {
                                    const val = e.target.value;
                                    setQuick(val);
                                    if (!val) return;
                                    const from = val === "today" ? todayStr() : startOf(val);
                                    const to = todayStr();
                                    setDateFrom(from);
                                    setDateTo(to);
                                    setApplied({ startDate: from, endDate: to });
                                }}>
                                <option value="">Quick select</option>
                                <option value="today">Today</option>
                                <option value="week">This Week</option>
                                <option value="month">This Month</option>
                                <option value="quarter">This Quarter</option>
                                <option value="year">This Year</option>
                            </select>
                        </div>
                        <div className="ov-filter-divider" />
                        <button className="ov-btn-apply"
                            onClick={() => setApplied({ startDate: dateFrom, endDate: dateTo })}>
                            Apply Filters
                        </button>
                        <button className="ov-btn-reset"
                            onClick={() => {
                                const s = startOf("month"), e = todayStr();
                                setDateFrom(s); setDateTo(e); setQuick("month");
                                setApplied({ startDate: s, endDate: e });
                            }}>
                            Reset
                        </button>
                    </div>

                    {/* ── Metrics ── */}
                    <div className="ov-metrics-grid">
                        <MetricCard
                            loading={dashLoading}
                            iconBg="#e1f5ee" iconStroke="#0f6e56"
                            iconPath={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>}
                            label="Total Patients"
                            value={fmtN(m?.totalPatients?.count)}
                            change={m?.totalPatients?.weeklyChangePercent != null
                                ? `${m.totalPatients.weeklyChangePercent > 0 ? "+" : ""}${m.totalPatients.weeklyChangePercent}% this week`
                                : ""}
                            changeType={m?.totalPatients?.weeklyChangePercent >= 0 ? "up" : "down"}
                        />
                        <MetricCard
                            loading={dashLoading}
                            iconBg="#dbeafe" iconStroke="#185fa5"
                            iconPath={<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>}
                            label="Appointments Today"
                            value={fmtN(m?.appointmentsToday?.total)}
                            change={m?.appointmentsToday != null
                                ? `${m.appointmentsToday.completed} completed · ${m.appointmentsToday.upcoming} upcoming`
                                : ""}
                            changeType="up"
                        />
                        <MetricCard
                            loading={dashLoading}
                            iconBg="#fef3c7" iconStroke="#b45309"
                            iconPath={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>}
                            label="Reports Pending"
                            value={fmtN(m?.reportsPending?.count)}
                            change={m?.reportsPending?.deltaSinceYesterday != null
                                ? `${m.reportsPending.deltaSinceYesterday > 0 ? "+" : ""}${m.reportsPending.deltaSinceYesterday} since yesterday`
                                : ""}
                            changeType="wn"
                        />
                        <MetricCard
                            loading={dashLoading}
                            iconBg="#dcfce7" iconStroke="#16a34a"
                            iconPath={<path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />}
                            label="Revenue (MTD)"
                            value={INR(m?.revenueMtd?.amount)}
                            change={m?.revenueMtd?.changePercent != null
                                ? `${m.revenueMtd.changePercent > 0 ? "+" : ""}${m.revenueMtd.changePercent}% vs last month`
                                : ""}
                            changeType={m?.revenueMtd?.changePercent >= 0 ? "up" : "down"}
                        />
                    </div>

                    {/* ── Charts Row ── */}
                    <div className="ov-mid-row">
                        <div className="ov-card">
                            <div className="ov-card-head">
                                <span className="ov-card-title">Patient visits</span>
                                <select className="ov-card-filter" value={visitPeriod} onChange={e => setVisitPeriod(e.target.value)}>
                                    <option value="week">This week</option>
                                    <option value="month">This month</option>
                                    <option value="year">This year</option>
                                </select>
                            </div>
                            <div className="ov-legend-row">
                                <div className="ov-legend-item">
                                    <span className="ov-legend-dot" style={{ background: "#1d9e75" }} />Visits
                                </div>
                            </div>
                            <div className="ov-chart-line">
                                {visitsLoading
                                    ? <div className="ov-chart-empty">Loading…</div>
                                    : !visitsData?.labels?.length
                                        ? <div className="ov-chart-empty">No data</div>
                                        : <Line data={visitChartData} options={visitChartOpts} />
                                }
                            </div>
                        </div>

                        <div className="ov-card">
                            <div className="ov-card-head">
                                <span className="ov-card-title">Appointment types</span>
                            </div>
                            <div className="ov-legend-row">
                                {breakdown.map((b, i) => (
                                    <div key={b.type} className="ov-legend-item">
                                        <span className="ov-legend-dot" style={{ background: CONDITION_COLORS[i % CONDITION_COLORS.length] }} />
                                        {b.type} {b.percent}%
                                    </div>
                                ))}
                            </div>
                            <div className="ov-chart-donut">
                                {dashLoading
                                    ? <div className="ov-chart-empty">Loading…</div>
                                    : !breakdown.length
                                        ? <div className="ov-chart-empty">No appointments today</div>
                                        : <Doughnut data={apptChartData} options={apptChartOpts} />
                                }
                            </div>
                        </div>
                    </div>

                    {/* ── Bottom Row ── */}
                    <div className="ov-bot-row">

                        {/* Today's appointments */}
                        <div className="ov-card">
                            <div className="ov-card-head">
                                <span className="ov-card-title">Recent Appointments</span>
                                <button className="ov-view-all" onClick={() => setView("appointments")}>View all →</button>
                            </div>
                            <div className="ov-appt-list">
                                {dashLoading ? (
                                    <div className="ov-list-empty">Loading…</div>
                                ) : appts.length === 0 ? (
                                    <div className="ov-list-empty">No appointments today</div>
                                ) : appts.map((a, i) => {
                                    const { bg, fg } = AVATAR_PALETTE[i % AVATAR_PALETTE.length];
                                    const statusKey = (a.appointmentStatus || "").toLowerCase();
                                    const isTerminal = ["completed", "done", "cancelled", "canceled"].includes(statusKey);
                                    const effectiveStatus = !isTerminal && isApptTimePast(a.scheduledTime)
                                        ? "pending"
                                        : statusKey;
                                    return (
                                        <div key={a._id || i} className="ov-appt-item">
                                            <Avatar initials={a.initials} bg={bg} fg={fg} />
                                            <div className="ov-appt-info">
                                                <div className="ov-appt-name">{a.patientName}</div>
                                                <div className="ov-appt-sub">{a.appointmentType} · {a.doctorName}</div>
                                            </div>
                                            <div className="ov-appt-meta">
                                                <div className="ov-appt-time">{a.scheduledTime}</div>
                                                <StatusPill status={effectiveStatus} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Top drugs */}
                        <div className="ov-card">
                            <div className="ov-card-head">
                                <span className="ov-card-title">Top drugs</span>
                            </div>
                            <div className="ov-doc-list">
                                {dashLoading ? (
                                    <div className="ov-list-empty">Loading…</div>
                                ) : topDrugs.length === 0 ? (
                                    <div className="ov-list-empty">No drug data available</div>
                                ) : topDrugs.map((d, i) => (
                                    <div key={i} className="ov-doc-item">
                                        <div className="ov-drug-rank" style={{ background: AVATAR_PALETTE[i % AVATAR_PALETTE.length].bg, color: AVATAR_PALETTE[i % AVATAR_PALETTE.length].fg }}>
                                            #{d.rank}
                                        </div>
                                        <div className="ov-doc-info">
                                            <div className="ov-doc-name">{d.drugsName}</div>
                                            <div className="ov-doc-spec">{d.brand}</div>
                                        </div>
                                        <div className="ov-doc-stats">
                                            <div className="ov-doc-count">{d.prescriptionCount}</div>
                                            <div className="ov-doc-lbl">{d.uniquePatientCount} patients</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Top conditions + Bed occupancy */}
                        <div className="ov-card">
                            <div className="ov-card-head">
                                <span className="ov-card-title">Top conditions</span>
                                {/* <button className="ov-view-all">Details →</button> */}
                            </div>
                            <div className="ov-conditions-list">
                                {dashLoading ? (
                                    <div className="ov-list-empty">Loading…</div>
                                ) : conditions.length === 0 ? (
                                    <div className="ov-list-empty">No data</div>
                                ) : conditions.map((c, i) => (
                                    <div key={c.name} className="ov-condition-item">
                                        <div className="ov-condition-row">
                                            <span className="ov-condition-name">{c.name}</span>
                                            <span className="ov-condition-pct">{c.percent}%</span>
                                        </div>
                                        <div className="ov-bar-track">
                                            <div className="ov-bar-fill" style={{
                                                width: `${c.percent}%`,
                                                background: CONDITION_COLORS[i % CONDITION_COLORS.length],
                                            }} />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* <div className="ov-bed-section">
                                <div className="ov-bed-title">Bed occupancy</div>
                                {dashLoading ? (
                                    <div className="ov-list-empty">Loading…</div>
                                ) : beds.length === 0 ? (
                                    <div className="ov-list-empty">No bed data</div>
                                ) : (
                                    <div className="ov-bed-grid">
                                        {beds.map((b, i) => (
                                            <div key={b.ward || i} className="ov-bed-card">
                                                <div className="ov-bed-val">
                                                    {b.occupied}<span>/{b.total}</span>
                                                </div>
                                                <div className="ov-bed-label">{b.ward}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div> */}
                        </div>

                    </div>
                </main>
            </div>
        </div>
    );
}
